import { useParticipation } from "../funnel/useParticipation";
import { useMode } from "../panel/mode";
import { useFlagOr, useStructuralSetting, useT } from "../settings/context";
import { streamHidden } from "./onsite";

/**
 * LA VIDÉO DU DIRECT, ET SES QUATRE SOURCES.
 *
 * ⚠️ LA V1 DEVINE LA SOURCE PAR L'ORDRE DE SES `if` : Dacast d'abord, sinon Vimeo,
 * sinon YouTube, plus un `?mode=internal` non documenté dans l'adresse pour un flux
 * interne dont les deux URL sont ÉCRITES EN DUR et appartiennent à un seul client.
 * Ici la source est un réglage et l'adresse en est un autre : un client de plus ne
 * demande pas une livraison.
 *
 * ⚠️ RIEN N'EST AFFICHÉ TANT QUE L'ADRESSE MANQUE. Un `<iframe>` vers
 * `youtube.com/embed/undefined` affiche une erreur YouTube en plein milieu du site,
 * ce que la V1 fait quand le réglage n'est pas rempli.
 */

type Source = "youtube" | "vimeo" | "dacast" | "internal";

/* Des noms de marque, qui ne se traduisent pas. Le flux interne, lui, se dit dans la
   langue lue (voir le rendu). */
const LABELS: Record<Exclude<Source, "internal">, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  dacast: "Dacast",
};

/**
 * L'IDENTIFIANT D'UNE VIDÉO YOUTUBE, à partir de ce qu'on a bien voulu coller.
 *
 * ⚠️ UNE ADRESSE YOUTUBE NORMALE NE S'AFFICHE PAS DANS UN CADRE. `youtube.com/watch?v=…`
 * est refusée par YouTube (`X-Frame-Options`), et le cadre reste noir sans le moindre
 * message : seule la forme `/embed/…` est prévue pour ça. La première version de ce
 * fichier laissait passer toute adresse commençant par `https://`, donc coller le lien
 * de la barre d'adresse — le geste évident — donnait un rectangle vide.
 */
export function youtubeId(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;

  const patterns = [
    /[?&]v=([\w-]{6,})/, // youtube.com/watch?v=ID
    /youtu\.be\/([\w-]{6,})/, // youtu.be/ID
    /\/embed\/([\w-]{6,})/, // déjà une adresse d'insertion
    /\/live\/([\w-]{6,})/, // youtube.com/live/ID
    /\/shorts\/([\w-]{6,})/,
  ];

  for (const pattern of patterns) {
    const found = raw.match(pattern);
    if (found?.[1]) return found[1];
  }

  // Pas une adresse : c'est l'identifiant nu, ce que la V1 demandait.
  return /^[\w-]{6,}$/.test(raw) ? raw : null;
}

/** Même chose pour Vimeo, dont l'adresse publique n'est pas non plus insérable. */
export function vimeoId(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  const found = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (found?.[1]) return found[1];
  return /^\d+$/.test(raw) ? raw : null;
}

/** L'adresse d'affichage, à partir de ce que la plateforme fournit. */
export function embedUrl(source: Source, value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;

  if (source === "youtube") {
    const id = youtubeId(raw);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : null;
  }

  if (source === "vimeo") {
    const id = vimeoId(raw);
    if (id) return `https://player.vimeo.com/video/${id}`;
    // Certains comptes servent une adresse d'insertion complète, à garder telle quelle.
    return /^https?:\/\/player\.vimeo\.com\//i.test(raw) ? raw : null;
  }

  if (source === "dacast") {
    const found = raw.match(/dacast\.com\/live\/(.+)$/);
    if (found?.[1]) return `https://iframe.dacast.com/live/${found[1]}`;
    if (/^https?:\/\//i.test(raw)) return raw;
    /**
     * ⚠️ ENCODÉ SEGMENT PAR SEGMENT, PAS D'UN BLOC. Un identifiant Dacast contient
     * lui-même une barre oblique (`183a…/18731683…`) : un `encodeURIComponent` sur
     * la valeur entière la change en `%2F` et l'adresse ne désigne plus rien. Vu
     * sur un vrai événement, qui affichait un lecteur vide.
     */
    return `https://iframe.dacast.com/live/${raw
      .split("/")
      .map(encodeURIComponent)
      .join("/")}`;
  }

  // Flux interne : il n'a pas de forme déductible, il faut l'adresse entière.
  return /^https?:\/\//i.test(raw) ? raw : null;
}

export function Video({
  viewers,
  preview = false,
}: {
  viewers?: number | null;
  /**
   * ⚠️ L'APERÇU DE LA RÉGIE NE LIT PAS LE FLUX. L'organisateur pilote très souvent
   * depuis la machine qui projette : monter le lecteur une seconde fois y ouvrait un
   * second flux, avec son son, sur le poste le moins disponible de la salle.
   * L'aperçu montre l'emplacement, pas la vidéo, et c'est la seule chose que la
   * régie voit autrement que le participant.
   */
  preview?: boolean;
}) {
  const t = useT();
  const shown = useFlagOr("FO_MEETING_V2_LIVE_VIDEO");
  const countShown = useFlagOr("FO_MEETING_V2_LIVE_COUNT");
  /* Inscrit en présentiel, on est dans la salle : pas de vidéo (voir `streamHidden`).
     La participation est déjà connue ici — le châssis l'attend avant de rendre une page. */
  const { recorded } = useParticipation();
  const organiser = useMode() === "organiser";
  const inTheRoom = streamHidden({
    answer: recorded?.answer,
    organiser,
    preview,
  });

  /**
   * ⚠️ LUES HORS DE LA CASCADE DES LANGUES, les six. Une adresse de flux est la
   * même vidéo dans toutes les langues, mais elle est ENREGISTRÉE dans la ligne de
   * la langue où l'organisateur se trouvait. Lue normalement, elle n'atteint que
   * les participants qui lisent cette langue-là — et sur un événement dont la
   * langue de référence est l'anglais, une adresse saisie en français n'atteint
   * personne. Voir `useStructuralSetting`.
   */
  const chosenSource = useStructuralSetting("FO_MEETING_V2_LIVE_SOURCE");
  const chosenUrl = useStructuralSetting("FO_MEETING_V2_LIVE_URL");

  /**
   * ⚠️ LE RÉGLAGE V2, ET RIEN D'AUTRE. L'écran reprenait en silence la vidéo de la V1
   * (`youtubeId`, `vimeoUrl`, `FO_MEETING_DACAST_ID`) quand l'adresse était vide :
   * l'organisateur voyait un champ vide dans le panneau et une vidéo sur le direct, sans
   * rien pour relier les deux (vu sur TDE-28-2026, le 2026-09-26). Ce qu'affiche le site
   * doit se lire dans le panneau. Une valeur V1 n'entre que par la reprise, qui l'ÉCRIT
   * dans le réglage V2 — donc sous les yeux de l'organisateur.
   */
  const active =
    typeof chosenUrl === "string" && chosenUrl.trim()
      ? {
          source: ((chosenSource as Source) || "youtube") as Source,
          url: chosenUrl,
        }
      : null;
  const url = active ? embedUrl(active.source, active.url) : null;

  if (!shown) return null;

  /* ⚠️ PAS D'ADRESSE, PAS DE CADRE : un rectangle noir marqué « EN DIRECT » se lit comme
     une vidéo qui ne charge pas. Masqué plutôt qu'absent, comme les bandeaux du site :
     le bloc nommé reste dans le DOM, et c'est lui que le panneau vise. Même chose pour
     qui est dans la salle : le module interactif prend alors toute la place. */
  if (!url || !active || inTheRoom) {
    return <div className="live__v" data-cfg="liveVideo" hidden />;
  }

  return (
    <div className="live__v" data-cfg="liveVideo">
      {preview ? (
        <span className="live__p">
          La vidéo se joue sur l'écran du participant, sauf s'il est inscrit en présentiel.
        </span>
      ) : (
        <iframe
          className="live__f"
          src={url}
          title={t("LIVE_STREAMED", "Diffusion en direct")}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
      <span className="live__b">{t("LIVE_BADGE", "EN DIRECT")}</span>
      <span className="live__s">
        {active.source === "internal"
          ? t("INTERNAL_STREAM", "Flux interne")
          : LABELS[active.source]}
      </span>
      {countShown && typeof viewers === "number" ? (
        <span className="live__n">
          {viewers > 1
            ? t("N_WATCHING_MANY", "$1 personnes regardent", [viewers])
            : t("N_WATCHING_ONE", "$1 personne regarde", [viewers])}
        </span>
      ) : null}
    </div>
  );
}
